import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '@/utils/api';

const SITE_URL = process.env.REACT_APP_BASE_URL || 'https://www.houseplandirect.com';

/**
 * Generate canonical URL from pathname
 * Rules:
 * - Strip query params and hash
 * - Remove trailing slash except for homepage
 * - Always lowercase
 */
const getCanonicalUrl = (pathname) => {
  // Remove query params and hash
  let cleanPath = pathname.split('?')[0].split('#')[0];
  
  // Remove trailing slash except for homepage
  if (cleanPath !== '/' && cleanPath.endsWith('/')) {
    cleanPath = cleanPath.slice(0, -1);
  }
  
  return `${SITE_URL}${cleanPath}`;
};

/**
 * Imperatively create/update a <meta> tag matched by attribute (name or property).
 * The initial static tags shipped in public/index.html are NOT rendered by React,
 * so React 19's native metadata hoisting cannot dedupe against them - it simply
 * appends new tags, leaving the old static ones in place (and first in document
 * order, which is what crawlers/tools read). We instead update the existing DOM
 * node in place (creating it only if genuinely absent) so there is always exactly
 * one instance of each singleton tag, and it always reflects the current page.
 */
const upsertMeta = (attr, value, content) => {
  if (content === undefined || content === null) return;
  let el = document.querySelector(`meta[${attr}="${value}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const SEO = ({ pageId, defaultTitle, defaultDescription, aiSummary, keywords, noIndex }) => {
  const location = useLocation();
  const [seoData, setSeoData] = useState(null);
  const [globalSettings, setGlobalSettings] = useState(null);

  useEffect(() => {
    fetchSEOSettings();
  }, [pageId]);

  const fetchSEOSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/seo-settings`);
      setGlobalSettings(response.data);
      
      // Get page-specific SEO data
      if (response.data.pages && response.data.pages[pageId]) {
        setSeoData(response.data.pages[pageId]);
      }
    } catch (error) {
      console.error('Error fetching SEO settings:', error);
    }
  };

  const title = seoData?.page_title || defaultTitle;
  const description = seoData?.meta_description || defaultDescription;
  
  // Generate canonical URL from current location
  const canonical = seoData?.canonical_url || getCanonicalUrl(location.pathname);
  
  const ogTitle = seoData?.og_title || title;
  const ogDescription = seoData?.og_description || description;
  const ogImage = seoData?.og_image_url || `${SITE_URL}/logo.jpg`;
  const pageKeywords = keywords || 'floor plans, CAD conversion, sketch to CAD, UK floor plans';

  useEffect(() => {
    document.title = title;
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', pageKeywords);
    upsertMeta('name', 'robots', noIndex ? 'noindex, follow' : 'index, follow');
    upsertCanonical(canonical);

    if (aiSummary) upsertMeta('name', 'ai-summary', aiSummary);

    upsertMeta('property', 'og:title', ogTitle);
    upsertMeta('property', 'og:description', ogDescription);
    upsertMeta('property', 'og:image', ogImage);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', 'House Plan Direct');
    upsertMeta('property', 'og:locale', 'en_GB');

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', ogTitle);
    upsertMeta('name', 'twitter:description', ogDescription);
    upsertMeta('name', 'twitter:image', ogImage);
    upsertMeta('name', 'twitter:site', '@HousePlanDirect');

    if (globalSettings?.google_search_console_tag) {
      upsertMeta('name', 'google-site-verification', globalSettings.google_search_console_tag);
    }
  }, [title, description, canonical, ogTitle, ogDescription, ogImage, pageKeywords, aiSummary, globalSettings, noIndex]);

  // Google Analytics 4 - inject once per analytics id, guarded against duplicates
  useEffect(() => {
    const gaId = globalSettings?.google_analytics_id;
    if (!gaId || document.getElementById('ga4-gtag-src')) return;

    const gtagScript = document.createElement('script');
    gtagScript.id = 'ga4-gtag-src';
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(gtagScript);

    const inlineScript = document.createElement('script');
    inlineScript.id = 'ga4-gtag-config';
    inlineScript.text = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${gaId}');
    `;
    document.head.appendChild(inlineScript);
  }, [globalSettings]);

  // JSON-LD schema blocks (BreadcrumbSchema, FAQSchema, HowToSchema, SchemaOrg) have no
  // static equivalent in index.html, so plain React rendering is safe here - no duplicate risk.
  return null;
};

export default SEO;
